import { Data, Option, Schema } from 'effect';

import type { ViolationField } from '../data/error/backendApi/_internal/createBackendApiError';
import {
  isNotFoundError,
  isUnauthorizedError,
  isViolationError,
} from '../data/error/backendApi/_internal/createBackendApiError';

/**
 * The three things that can go wrong reading a capability, in the vocabulary a
 * loader branches on.
 *
 * A loader must decide between "there is nothing at this address", "the shop is
 * up but this read did not work" and "the backend answered something the
 * contract does not describe". Those three carry different pages: a 404, a
 * retryable failure notice, and a failure notice plus an alert for whoever owns
 * the contract. Nothing else about a backend's error vocabulary changes what a
 * page renders, so nothing else is modelled here.
 *
 * A tenant's own error union never leaves its adapter. The adapter maps it onto
 * these three with {@link makeCapabilityErrors}'s `fromRequestFailure`, which is
 * the one classifier in the programme — so two tenants cannot disagree about
 * what a 500 means.
 *
 * Errors are per capability rather than shared, so a page reading two
 * capabilities can still say which one failed. The tags read
 * `@engine/Capability/<Capability>/<Outcome>`.
 */
export type CapabilityFailure = {
  /** The service method that failed, e.g. `taxon` or `products`. */
  readonly operation: string;
  /** What the call asked for — a taxon code, a product slug, a page number. */
  readonly reference: Option.Option<string>;
  /** The tenant-side failure this was mapped from. Logged, never matched on. */
  readonly cause: unknown;
};

/** One capability's failure as its error messages read it. */
const describeFailure =
  (capability: string) =>
  (outcome: string, failure: CapabilityFailure): string =>
    `${capability}.${failure.operation} ${outcome}${Option.match(failure.reference, {
      onNone: () => '',
      onSome: (reference) => `: ${reference}`,
    })}`;

/**
 * The tagged errors one capability reports, and the classifier its adapter maps
 * a request failure through.
 *
 * ```ts
 * export const CategoryListingError = makeCapabilityErrors('CategoryListing');
 * export type CategoryListingError = CapabilityErrorUnion<typeof CategoryListingError>;
 * ```
 *
 * `fromRequestFailure` is what keeps a provider to one line per outcome. It
 * reads the failure the engine's request template produced and answers the
 * neutral error for it:
 *
 * - a 404 from the operation's error family is `NotFound`;
 * - a schema decode failure is `ContractMismatch`;
 * - everything else — any other status, a network failure, a request the client
 *   could not build — is `Unavailable`.
 *
 * The last arm is deliberately the default rather than an enumeration. A read
 * that fails for a reason nobody has seen yet is still a read the shopper cannot
 * have, and `Unavailable` is the honest page for it. Widening the enumeration
 * instead would leave a new failure unclassified and the error channel open.
 */
export const makeCapabilityErrors = <const Capability extends string>(capability: Capability) => {
  const notFoundTag = `@engine/Capability/${capability}/NotFound` as const;
  const unavailableTag = `@engine/Capability/${capability}/Unavailable` as const;
  const contractMismatchTag = `@engine/Capability/${capability}/ContractMismatch` as const;

  const describe = describeFailure(capability);

  /** The backend has no such resource. The page is a 404. */
  class NotFound extends Data.TaggedError(notFoundTag)<CapabilityFailure> {
    override get message(): string {
      return describe('not found', this);
    }
  }

  /** The read did not complete: any non-404 status, or the network. */
  class Unavailable extends Data.TaggedError(unavailableTag)<CapabilityFailure> {
    override get message(): string {
      return describe('unavailable', this);
    }
  }

  /** The backend answered a body the curated schema rejects. */
  class ContractMismatch extends Data.TaggedError(contractMismatchTag)<CapabilityFailure> {
    override get message(): string {
      return describe('contract mismatch', this);
    }
  }

  const fromRequestFailure =
    (operation: string, reference?: string) =>
    (cause: unknown): NotFound | Unavailable | ContractMismatch => {
      const failure: CapabilityFailure = {
        operation,
        reference: Option.fromNullishOr(reference),
        cause,
      };

      if (isNotFoundError(cause)) return new NotFound(failure);
      if (Schema.isSchemaError(cause)) return new ContractMismatch(failure);
      return new Unavailable(failure);
    };

  return {
    capability,
    NotFound,
    Unavailable,
    ContractMismatch,
    fromRequestFailure,
    Tags: {
      NotFound: notFoundTag,
      Unavailable: unavailableTag,
      ContractMismatch: contractMismatchTag,
    },
  } as const;
};

/** The shape {@link makeCapabilityErrors} returns, for constraining a family. */
export type CapabilityErrors = ReturnType<typeof makeCapabilityErrors>;

/** The error union one capability reports, derived from its family. */
export type CapabilityErrorUnion<T extends CapabilityErrors> =
  | InstanceType<T['NotFound']>
  | InstanceType<T['Unavailable']>
  | InstanceType<T['ContractMismatch']>;

/**
 * What a refused write carries beyond a read failure: the backend's own words,
 * and the codes it filed them under.
 *
 * `messages` is the refusal as the shop wrote it, in the order it wrote it. A
 * page renders them beside the control the shopper used. **It stays the only
 * thing a page renders.** The codes a backend sends are its own, two backends
 * that refuse the same thing do not agree on one, and a sentence a shopper can
 * read is what both do send.
 *
 * `fields` is the subset of that refusal which named the control it is about.
 * The cart fills none — every violation it sends carries `propertyPath: ""` —
 * and the customer capabilities fill it: registering with a taken address
 * answers `propertyPath: "email"`, and a short password answers three
 * violations naming three controls. A form marks those controls from this list
 * and renders the rest of `messages` above itself. A page with one control
 * reads `messages` and ignores this.
 *
 * `codes` is the same refusal for a caller that must ACT on it rather than show
 * it, and those are different needs. Two of Sylius's codes say the order can no
 * longer be written to at all — `requiresNewTokenViolationCodes` names them —
 * and a caller holding that order's token must forget it rather than render a
 * sentence about it. Nothing in `messages` carries that fact in a form a
 * program can read.
 *
 * **A backend that sends no codes answers an empty list, and this arm is then
 * silent rather than wrong.** RacingLine is such a backend: every 422 captured
 * from it carries `code: null` on every violation, so its refusals arrive here
 * with `messages` full and `codes` empty. Whoever needs the fact `codes` would
 * have carried must get it from the order itself —
 * `capability/cart/cartToken.ts` reads the cart, and that is why it does.
 */
export type CapabilityRejection = CapabilityFailure & {
  readonly messages: ReadonlyArray<string>;
  readonly fields: ReadonlyArray<ViolationField>;
  readonly codes: ReadonlyArray<string>;
};

/**
 * The tagged errors one WRITE capability reports, and the classifier its adapter
 * maps a write failure through.
 *
 * ```ts
 * export const CartError = makeWriteCapabilityErrors('Cart');
 * export type CartError = CapabilityErrorUnion<typeof CartError>;
 * export type CartWriteError = CapabilityWriteErrorUnion<typeof CartError>;
 * ```
 *
 * It is the read family plus one arm. A write can be refused, and a read cannot:
 * there is no way to ask for a taxon wrongly, and every way to ask for a cart
 * line wrongly. So `Rejected` exists here and nowhere else, and a capability
 * that only reads keeps the three-error family it has.
 *
 * The two classifiers are both here because a capability that writes usually
 * reads as well. `fromRequestFailure` types a read's operations with the three
 * read errors, and `fromWriteFailure` types a write's with four. A page reading
 * a cart it never changes handles three arms, not four.
 *
 * `fromWriteFailure` answers:
 *
 * - a 422 carrying a violation list is `Rejected`, with the messages and the
 *   codes the backend filed them under;
 * - a 404 — and a 422 carrying no violation list, which the write template has
 *   already turned into the family's `NotFoundError` — is `NotFound`;
 * - a schema decode failure is `ContractMismatch`;
 * - everything else, including a `409` the retry policy could not clear, is
 *   `Unavailable`.
 *
 * A conflict is not its own arm. A Sylius optimistic-lock collision rolls the
 * transaction back and the shopper can do nothing about it, so an exhausted
 * retry is the same page as any other failure the shopper cannot act on.
 *
 * `Unauthorized` is not an arm either, and it is the one this family will grow.
 * A write that requires a signed-in customer needs it, because a sign-in prompt
 * is a different page from a failure notice. No cart operation can produce one,
 * so it arrives with the first capability whose backend answers `401`;
 * `isUnauthorizedError` is already the classifier for it.
 */
export const makeWriteCapabilityErrors = <const Capability extends string>(
  capability: Capability
) => {
  const read = makeCapabilityErrors(capability);
  const rejectedTag = `@engine/Capability/${capability}/Rejected` as const;

  const describe = describeFailure(capability);

  /** The shop refused what was asked, and said why. The resource is unchanged. */
  class Rejected extends Data.TaggedError(rejectedTag)<CapabilityRejection> {
    override get message(): string {
      const said = this.messages.length === 0 ? '' : ` — ${this.messages.join('; ')}`;
      return `${describe('rejected', this)}${said}`;
    }
  }

  const fromWriteFailure =
    (operation: string, reference?: string) =>
    (
      cause: unknown
    ):
      | InstanceType<typeof read.NotFound>
      | InstanceType<typeof read.Unavailable>
      | InstanceType<typeof read.ContractMismatch>
      | Rejected => {
      const failure: CapabilityFailure = {
        operation,
        reference: Option.fromNullishOr(reference),
        cause,
      };

      if (isViolationError(cause))
        return new Rejected({
          ...failure,
          messages: cause.messages,
          fields: cause.fields,
          codes: cause.codes,
        });
      if (isNotFoundError(cause)) return new read.NotFound(failure);
      if (Schema.isSchemaError(cause)) return new read.ContractMismatch(failure);
      return new read.Unavailable(failure);
    };

  return {
    ...read,
    Rejected,
    fromWriteFailure,
    Tags: { ...read.Tags, Rejected: rejectedTag },
  } as const;
};

/** The shape {@link makeWriteCapabilityErrors} returns, for constraining a family. */
export type CapabilityWriteErrors = ReturnType<typeof makeWriteCapabilityErrors>;

/** The error union one capability's writes report, derived from its family. */
export type CapabilityWriteErrorUnion<T extends CapabilityWriteErrors> =
  | CapabilityErrorUnion<T>
  | InstanceType<T['Rejected']>;

/**
 * The tagged errors one capability reports when its backend can answer `401`,
 * and the two classifiers its adapter maps a failure through.
 *
 * ```ts
 * export const CustomerSessionError = makeSessionCapabilityErrors('CustomerSession');
 * export type CustomerSessionError = CapabilitySessionErrorUnion<typeof CustomerSessionError>;
 * export type CustomerSessionWriteError =
 *   CapabilitySessionWriteErrorUnion<typeof CustomerSessionError>;
 * ```
 *
 * It is the write family plus one arm. `.ai/plan/ENGINE_WRITE_TIER.md` named
 * `Unauthorized` and did not build it, because no cart operation can produce
 * one: a Sylius cart is anonymous and the whole cart surface answers `401` for
 * nothing. The customer capabilities are the first that can, and they produce
 * it two different ways — a sign-in with the wrong password, and any read of
 * `/customers/me` without a bearer token. Both were measured against the live
 * RacingLine backend on 2026-09-08.
 *
 * **Its page is a sign-in prompt, not a failure notice.** That is the whole
 * reason it is an arm rather than an `Unavailable`. A shopper whose session
 * expired can do something — sign in — and a failure notice tells them the shop
 * is broken when it is not. The same page serves both ways it arises: the
 * refused sign-in renders the sign-in form carrying the shop's own sentence,
 * and the expired session renders the sign-in form carrying nothing.
 *
 * The arm sits on this family rather than on {@link makeCapabilityErrors} so
 * the existing read capabilities keep the three-error union they have. A
 * catalogue read cannot answer `401` on either backend, and widening its union
 * would make every catalogue loader handle an arm it can never receive.
 *
 * Both classifiers test for `401` before delegating, so a `401` is
 * `Unauthorized` whether it arrived on a read or on a write. Everything else
 * classifies exactly as the write family classifies it.
 */
export const makeSessionCapabilityErrors = <const Capability extends string>(
  capability: Capability
) => {
  const write = makeWriteCapabilityErrors(capability);
  const unauthorizedTag = `@engine/Capability/${capability}/Unauthorized` as const;

  const describe = describeFailure(capability);

  /** The caller is not who this resource is for. The page is a sign-in prompt. */
  class Unauthorized extends Data.TaggedError(unauthorizedTag)<CapabilityFailure> {
    override get message(): string {
      return describe('unauthorized', this);
    }
  }

  type ReadFailure =
    | InstanceType<typeof write.NotFound>
    | InstanceType<typeof write.Unavailable>
    | InstanceType<typeof write.ContractMismatch>;

  const failureOf = (operation: string, reference: string | undefined, cause: unknown) => ({
    operation,
    reference: Option.fromNullishOr(reference),
    cause,
  });

  const fromRequestFailure =
    (operation: string, reference?: string) =>
    (cause: unknown): ReadFailure | Unauthorized =>
      isUnauthorizedError(cause)
        ? new Unauthorized(failureOf(operation, reference, cause))
        : write.fromRequestFailure(operation, reference)(cause);

  const fromWriteFailure =
    (operation: string, reference?: string) =>
    (cause: unknown): ReadFailure | InstanceType<typeof write.Rejected> | Unauthorized =>
      isUnauthorizedError(cause)
        ? new Unauthorized(failureOf(operation, reference, cause))
        : write.fromWriteFailure(operation, reference)(cause);

  return {
    ...write,
    Unauthorized,
    fromRequestFailure,
    fromWriteFailure,
    Tags: { ...write.Tags, Unauthorized: unauthorizedTag },
  } as const;
};

/** The shape {@link makeSessionCapabilityErrors} returns, for constraining a family. */
export type CapabilitySessionErrors = ReturnType<typeof makeSessionCapabilityErrors>;

/**
 * The error union one session capability's READS report, derived from its
 * family.
 *
 * Spelled out rather than composed from {@link CapabilityErrorUnion}: a session
 * family's classifiers return a wider union than a read family's, so it does
 * not satisfy that type's `CapabilityErrors` constraint. The four arms are the
 * three read arms plus `Unauthorized`.
 */
export type CapabilitySessionErrorUnion<T extends CapabilitySessionErrors> =
  | InstanceType<T['NotFound']>
  | InstanceType<T['Unavailable']>
  | InstanceType<T['ContractMismatch']>
  | InstanceType<T['Unauthorized']>;

/** The error union one session capability's WRITES report, derived from its family. */
export type CapabilitySessionWriteErrorUnion<T extends CapabilitySessionErrors> =
  | CapabilitySessionErrorUnion<T>
  | InstanceType<T['Rejected']>;
